import { AfterViewInit, Component, computed, OnDestroy, OnInit, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { NavigationEnd, Router } from "@angular/router";
import { AppIdentity, UserInfoType } from "@evc/platform";
import { BtnActionEnum, Menu, MenuItemType, ThemeService, ThemeType, ValueOf } from "@evc/web-components";
import { TranslateService } from "@ngx-translate/core";
import { filter, map, Subject, take, takeUntil } from "rxjs";
import { TenantDetail } from "../_objects/offline-management";
import { ConfigurationItem } from "../_objects/tenant-config";
import { User } from "../_objects/user";
import { FeatureFlagService } from "../_services/feature-flag/feature-flag.service";
import { LoginUserOnlineService } from "../_services/http-api/login-user-online.service";
import { MeUserService } from "../_services/http-api/me-user.service";
import { TenantConfigService } from "../_services/http-api/tenant-config.service";
import { ApplicationMode, NetworkStatusService } from "../_services/network-status.service";
import { isDevelopment } from "../_shared/environment-helper";
import { EvcModalService } from "../_shared/services/modal/evc-modal.service";
import { RoutePage, RouteSettings } from "../app-routes.enum";
import { FEATURE_FLAGS, VERSION } from "../app.constants";
import { QRCodeSupportPageComponent } from "../qrcode-support-page/qrcode-support-page.component";
import { NavigationMenuService } from "../shared/services/navigation-menu/navigation-menu.service";
import { TopBarMenuService } from "./top-bar-menu.service";

type MenuItemExtended = MenuItemType & { isVisible: boolean };

export const TOPBAR_LAYOUTS = {
  LEGACY: "legacy",
  PUBLIC: "public",
  PRIVATE: "private"
} as const;

export type TopbarLayout = ValueOf<keyof typeof TOPBAR_LAYOUTS>;

@Component({
  selector: "app-navigation-menu",
  templateUrl: "./navigation-menu.component.html",
  styleUrl: "./navigation-menu.component.scss",
  standalone: false
})
export class NavigationMenuComponent implements OnDestroy, OnInit, AfterViewInit {
  private onDestroy$ = new Subject<void>();
  private tenantConfigs = toSignal<ConfigurationItem[]>(this.tenantConfigurationService.tenantConfig$);
  public isOnline = toSignal(this.networkStatusService.isOnlineObserver());
  public applicationMode = toSignal(this.networkStatusService.applicationMode$);
  public featureFlag = this.featureFlagService.isFlagEnabled(FEATURE_FLAGS.ASSET_MODULE);
  private currentUrl = toSignal(
    this.router.events.pipe(
      filter((event) => event instanceof NavigationEnd),
      map(() => this.router.url)
    ),
    { initialValue: this.router.url }
  );

  public leftMenu = computed<Menu[]>(() => {
    return this.navigationMenuService.buildLeftMenu(
      this.isOnline(),
      this.tenantConfigs(),
      this.featureFlag(),
      this.isInSettingPage(),
      this.currentUrl()
    );
  });
  public helpMenu = signal<MenuItemType[]>(this.buildHelpMenu());
  public settingsMenu = computed<MenuItemType[]>(() => this.buildSettingsMenu(this.applicationMode()));

  public userProfile = this.getUserProfile();
  public isLeftbarOpen = signal<boolean>(true);
  public isDevelopment = signal<boolean>(isDevelopment());
  public isInSettingPage = signal(false);
  public topbarLayout: TopbarLayout = computed(() => this.setTopbarLayout());

  constructor(
    private networkStatusService: NetworkStatusService,
    private translate: TranslateService,
    private topBarMenuSettingsService: TopBarMenuService,
    private router: Router,
    private meUserService: MeUserService,
    private loginUserService: LoginUserOnlineService,
    private tenantConfigurationService: TenantConfigService,
    private navigationMenuService: NavigationMenuService,
    private themeService: ThemeService,
    private evcModalService: EvcModalService,
    private featureFlagService: FeatureFlagService
  ) {}

  ngOnInit(): void {
    this.subscribeToRouterEvents();
  }

  private setTopbarLayout() {
    if (!this.isOnline() || this.isOnPremise) {
      return TOPBAR_LAYOUTS.PRIVATE;
    }
    return TOPBAR_LAYOUTS.LEGACY;
  }

  private shouldShowSettingsMenu(): boolean {
    const settingsRoutes = [RouteSettings.Mark, RouteSettings.Title, RouteSettings.Tags, RouteSettings.TenantConfig];

    return this.router.url.includes(RoutePage.Settings) && settingsRoutes.some((route) => this.router.url.includes(route));
  }

  private subscribeToRouterEvents(): void {
    this.router.events
      .pipe(
        takeUntil(this.onDestroy$),
        filter((event) => event instanceof NavigationEnd)
      )
      .subscribe((event: NavigationEnd) => {
        this.isInSettingPage.set(this.shouldShowSettingsMenu());
      });
  }

  private getUserProfile() {
    return this.tenantConfigurationService.getTenantDetails().pipe(
      take(1),
      map((tenant) => this.buildUserProfile(tenant, this.loginUserService.loginUser))
    );
  }

  ngAfterViewInit(): void {
    this.themeService.setTheme((localStorage.getItem("app-theme") || "light") as ThemeType);
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.unsubscribe();
  }

  public appIdentity: AppIdentity = {
    logo: "./assets/applications/visol.svg#visol",
    title: this.translate.stream("topMenuBar.appTitle"),
    onTitleClick: () => this.router.navigate([RoutePage.IndexPage]),
    copyright: `${this.translate.instant("topMenuBar.version")} ${VERSION} - ${this.translate.instant("topMenuBar.copyrights")}`
  };

  private buildHelpMenu(): MenuItemType[] {
    return [
      {
        key: "support",
        text: this.translate.stream("topMenuBar.support"),
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => {
            if (this.isBrianApp) {
              return this.evcModalService.showModal(QRCodeSupportPageComponent);
            }
            return this.topBarMenuSettingsService.goToVisolHelp();
          }
        }
      },
      {
        key: "legal",
        text: this.translate.stream("topMenuBar.legal"),
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => this.topBarMenuSettingsService.openLegalDialog()
        }
      }
    ];
  }

  private buildSettingsMenu(applicationMode: ApplicationMode): MenuItemExtended[] {
    const isVisolOffline = NetworkStatusService.isVisolOffline(applicationMode);
    if (isVisolOffline) {
      return [];
    }

    const allMenuItems: MenuItemExtended[] = [
      {
        key: "downloadDesktopApp",
        text: this.translate.stream("topMenuBar.downloadDesktopApp"),
        isVisible: applicationMode.platform != "desktop",
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => this.topBarMenuSettingsService.downloadDesktopApp()
        }
      },
      {
        key: "sharePresetData",
        text: this.translate.stream("topMenuBar.presetData"),
        isVisible: this.isBrianApp,
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => this.topBarMenuSettingsService.sharePresetDataWithDevice()
        }
      },
      {
        key: "inspectionTemplate",
        text: this.translate.stream("topMenuBar.inspectionTemplate"),
        isVisible: this.isAdminRole && !this.featureFlag(),
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => this.router.navigate([RoutePage.Settings, RouteSettings.Subject])
        }
      },
      {
        key: "reportTemplate",
        text: this.translate.stream("topMenuBar.reportTemplate"),
        isVisible: this.isAdminRole,
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => this.gotoReportTemplateBuilder()
        }
      },
      {
        key: "advanced-app-settings",
        text: this.translate.stream("topMenuBar.advancedSettings"),
        isVisible: this.isAdminRole,
        action: {
          type: BtnActionEnum.CALLBACK,
          callbackOrUrl: () => this.router.navigate([RoutePage.Settings, RouteSettings.TenantConfig])
        }
      },
      {
        isVisible: true,
        text: this.translate.stream("topMenuBar.settings.theme.text"),
        key: "theme",
        collapsible: [
          {
            text: this.translate.stream("topMenuBar.settings.theme.light"),
            key: "light",
            action: {
              type: BtnActionEnum.CALLBACK,
              callbackOrUrl: (): void => {
                this.themeService.setTheme("light");
                localStorage.setItem("app-theme", "light");
              }
            },
            img: "./assets/theme/light.webp"
          },
          {
            text: this.translate.stream("topMenuBar.settings.theme.dark"),
            key: "dark",
            action: {
              type: BtnActionEnum.CALLBACK,
              callbackOrUrl: (): void => {
                this.themeService.setTheme("dark");
                localStorage.setItem("app-theme", "dark");
              }
            },
            img: "./assets/theme/dark.webp"
          }
        ]
      }
    ];

    const items = allMenuItems.filter((item) => item.isVisible);

    return items;
  }

  private gotoReportTemplateBuilder(): void {
    const applicationMode = this.applicationMode();
    if (applicationMode?.platform == "desktop" && applicationMode?.mode == "onPremise") {
      this.router.navigate([RoutePage.Settings, RouteSettings.OfflineReportTemplate]);
    } else {
      window.open(this.meUserService.getReportTemplateBuilderEndpoint(["template-list"], "?application=ViSOL"), "_blank");
    }
  }

  private get isBrianApp() {
    return this.networkStatusService.isBrianMode;
  }

  private get isAdminRole() {
    const applicationMode = this.applicationMode();
    return this.loginUserService.isTenantAdminRole() || (applicationMode?.platform == "desktop" && applicationMode?.mode == "onPremise");
  }

  private buildUserProfile(tenant: TenantDetail, user: User): UserInfoType | undefined {
    return this.networkStatusService.isOnline
      ? {
          id: user?.id,
          tenantId: this.loginUserService.tenantAttribute?.id || tenant?.id,
          displayName: user?.givenName || " ",
          givenName: user?.givenName,
          surname: user?.surname,
          email: user?.email,
          role: this.loginUserService.tenantAttribute?.role.toLowerCase(),
          telephoneNumber: user?.telephoneNumber,
          organizations: [],
          applications: [],
          color: "#93c6f9"
        }
      : undefined;
  }

  public toggleLeftBar(isLeftbarOpen: boolean) {
    this.isLeftbarOpen.set(isLeftbarOpen);
    this.navigationMenuService.toggleLeftBar(this.isLeftbarOpen());
  }

  public get isOnPremise() {
    return this.networkStatusService.isOnPremise;
  }
}
